/**
 * Generic any-field search matching.
 *
 * matchesSearch(obj, query) returns true when every whitespace-separated
 * token in `query` appears (case-insensitive substring) somewhere in the
 * object's values — including nested objects and arrays (depth-limited).
 *
 * Noise is excluded: null/booleans, `id`/`uid`/`*_id` keys (internal
 * identifiers — user-facing numbers like job_number/visit_number still
 * match), and storage URLs. ISO date strings also get human-formatted
 * variants appended so "jun 25" matches "2026-06-25T…".
 */
import { DateTime } from "luxon";

const skipKey = (key) =>
  key === "id" || key === "uid" || key.endsWith("_id") || key === "storage_url";

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

function collect(value, out, depth) {
  if (value == null || typeof value === "boolean") return;
  if (typeof value === "string") {
    out.push(value);
    if (ISO_DATE_RE.test(value)) {
      const dt = DateTime.fromISO(value);
      if (dt.isValid) {
        out.push(dt.toFormat("MMM d yyyy"), dt.toFormat("MMMM d yyyy"), dt.toFormat("M/d/yyyy"));
      }
    }
    return;
  }
  if (typeof value === "number") {
    out.push(String(value));
    return;
  }
  if (depth <= 0) return;
  if (Array.isArray(value)) {
    for (const v of value) collect(v, out, depth - 1);
    return;
  }
  if (typeof value === "object") {
    for (const [k, v] of Object.entries(value)) {
      if (skipKey(k)) continue;
      collect(v, out, depth - 1);
    }
  }
}

export function buildHaystack(obj, depth = 4) {
  const out = [];
  collect(obj, out, depth);
  return out.join(" ").toLowerCase();
}

export function matchesSearch(obj, query, depth = 4) {
  const tokens = (query || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const hay = buildHaystack(obj, depth);
  return tokens.every((t) => hay.includes(t));
}
